import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Eye, EyeOff, Trophy, Check } from "lucide-react"
import { useRegister } from "@/graphql/hooks/useAuth"
import Layout from "@/components/Layout/Layout"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { UserRegisterInput } from "@/graphql/types"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { validateRUT } from "@/lib/rutValidation"
import { LegalTerms } from "../Blog/Terms"

// --- Esquema de validación con Zod ---
const registerSchema = z
  .object({
    name: z.string().min(1, "Nombre requerido"),
    firstSurname: z.string().min(1, "Primer apellido requerido"),
    lastSurname: z.string().min(1, "Segundo apellido requerido"),
    email: z.string().email("Correo inválido"),
    phone: z.string().min(7, "Teléfono inválido"),
    birthday: z.string().min(1, "Fecha requerida"),
    genderId: z.number().min(1, "Género requerido"),
    documentType: z.string().min(1, "Tipo de documento requerido"),
    documentNumber: z.string().min(1, "Número de documento requerido"),
    passwd: z.string().min(8, "La contraseña debe tener al menos 8 caracteres"),
    confirmPassword: z.string().min(8, "Confirma tu contraseña"),
    address: z.string().min(1, "Dirección requerida"),
    countryId: z.number().min(1, "País requerido"),
    regionId: z.number().min(1, "Región requerida"),
    locationId: z.number().min(1, "Comuna requerida").optional().or(z.literal(0)),
  })
  .refine((data) => data.passwd === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  })
  .refine(
    (data) => {
      if (data.documentType === "RUT") {
        return validateRUT(data.documentNumber)
      }
      // Para otros tipos, validar que no tenga espacios ni caracteres especiales
      return /^[a-zA-Z0-9]+$/.test(data.documentNumber)
    },
    {
      message: "Número de documento inválido",
      path: ["documentNumber"],
    },
  )

type RegisterFormData = z.infer<typeof registerSchema>

const buildUserRegisterInput = (values): UserRegisterInput => {
  return {
    name: values.name || "Sin nombre",
    firstSurname: values.firstSurname || "Sin apellido",
    lastSurname: values.lastSurname || "Sin apellido",
    email: values.email || "noemail@example.com",
    passwd: values.passwd || "12345678",
    phone: values.phone || "",
    birthday: values.birthday || "2000-01-01",
    genderId: values.genderId || 1,
    categoryId: values.categoryId || 1,
    documentType: values.documentType,
    documentNumber: values.documentNumber,
    address: {
      name: "Casa",
      address: values.address || "Dirección no ingresada",
      countryId: values.countryId || 1,
      regionId: values.regionId || 1,
      locationId: values.locationId || 1,
    },
  }
}

const Register = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [terms, setTerms] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const navigate = useNavigate()
  const { register: registerUser, loading } = useRegister()

  const { countries, loading: countriesLoading } = useCountries()
  const [selectedCountry, setSelectedCountry] = useState(0)
  const [selectedRegion, setSelectedRegion] = useState(0)

  const { regions, loading: regionsLoading } = useRegions(selectedCountry)
  const {
    locations,
    loading: locationsLoading,
    refetchLocations,
  } = useLocations({
    regionId: selectedRegion,
  })

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      firstSurname: "",
      lastSurname: "",
      email: "",
      phone: "",
      birthday: "",
      genderId: 1,
      documentType: "RUT",
      documentNumber: "",
      passwd: "",
      confirmPassword: "",
      address: "",
      countryId: 0,
      regionId: 0,
      locationId: 0,
    },
  })

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const registerInput = buildUserRegisterInput(data)
      const result = await registerUser(registerInput)

      if (result?.status.success) {
        navigate("/")
      }
    } catch (error) {
      console.error("Registration error:", error)
    }
  }

  const passwordRequirements = [
    { text: "Al menos 8 caracteres", met: form.watch("passwd").length >= 8 },
    { text: "Una mayúscula", met: /[A-Z]/.test(form.watch("passwd")) },
    { text: "Una minúscula", met: /[a-z]/.test(form.watch("passwd")) },
    { text: "Un número", met: /\d/.test(form.watch("passwd")) },
  ]

  if (countriesLoading) {
    return <LoadingScreen />
  }

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-2xl bg-gradient-card border-border shadow-elevation">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
                <Trophy className="w-6 h-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Crear Cuenta</CardTitle>
            <CardDescription>Únete a la comunidad de Torneos MyL</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {/* Información Personal */}
                <h3 className="text-lg font-semibold">Datos personales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre *</FormLabel>
                        <FormControl>
                          <Input placeholder="Tu nombre" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="firstSurname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primer apellido *</FormLabel>
                        <FormControl>
                          <Input placeholder="Primer apellido" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastSurname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Segundo apellido *</FormLabel>
                        <FormControl>
                          <Input placeholder="Segundo apellido" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Documento de Identidad */}
                <h3 className="text-lg font-semibold">Documento de Identidad</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="documentType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de documento *</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona tipo de documento" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="RUT">RUT (Chile)</SelectItem>
                            <SelectItem value="CC">Cédula de Ciudadanía (CC)</SelectItem>
                            <SelectItem value="CI">Cédula de Identidad (CI)</SelectItem>
                            <SelectItem value="DNI">DNI</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="documentNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de documento *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              form.watch("documentType") === "RUT"
                                ? "12345678-9"
                                : "Número de documento"
                            }
                            {...field}
                          />
                        </FormControl>
                        {form.watch("documentType") === "RUT" && (
                          <p className="text-xs text-muted-foreground">
                            Formato: 12345678-9 (con o sin puntos y guión)
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Contacto */}
                <h3 className="text-lg font-semibold">Datos de Contacto</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Correo electrónico *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="tu@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono *</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="birthday"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha de nacimiento *</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="genderId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Género *</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona tu género" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">Masculino</SelectItem>
                            <SelectItem value="2">Femenino</SelectItem>
                            <SelectItem value="3">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <h3 className="text-lg font-semibold">Dirección</h3>
                {/* Dirección */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dirección *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ingresa tu dirección acá..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="countryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>País</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => {
                            const countryId = parseInt(value)
                            field.onChange(countryId)
                            setSelectedCountry(countryId)
                            setSelectedRegion(0)
                            form.setValue("regionId", 0)
                            form.setValue("locationId", 0)
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar país" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country.id} value={country.id.toString()}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="regionId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Región</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => {
                            const regionId = parseInt(value)
                            setSelectedRegion(regionId)
                            refetchLocations(regionId)
                            field.onChange(regionId)
                            form.setValue("locationId", 0)
                          }}
                          disabled={!selectedCountry || regionsLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar región" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {regions.map((region) => (
                              <SelectItem key={region.id} value={region.id.toString()}>
                                {region.fullName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="locationId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Comuna</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          disabled={!selectedRegion || locationsLoading || !locations.length}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar comuna" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem key={location.id} value={location.id.toString()}>
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Contraseñas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="passwd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contraseña *</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              {...field}
                              className="pr-10"
                            />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmar contraseña *</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="••••••••"
                              {...field}
                              className="pr-10"
                            />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Requisitos de contraseña */}
                {form.watch("passwd") && (
                  <div className="space-y-1 mt-2">
                    {passwordRequirements.map((req, index) => (
                      <div key={index} className="flex items-center space-x-2 text-xs">
                        <Check
                          className={`w-3 h-3 ${req.met ? "text-green-500" : "text-muted-foreground"}`}
                        />
                        <span className={req.met ? "text-green-500" : "text-muted-foreground"}>
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Checkbox para aceptar términos */}
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox onClick={() => setTerms(!terms)} />
                  <span className="text-sm">
                    Acepto los{" "}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button type="button" className="text-blue-500 underline">
                          Términos y Condiciones
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Términos y Condiciones</DialogTitle>
                        </DialogHeader>
                        <LegalTerms className="max-w-full mx-auto p-4 md:p-6 bg-white rounded-2xl shadow-sm overflow-y-auto max-h-[70vh]" />
                      </DialogContent>
                    </Dialog>
                  </span>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-primary shadow-glow hover:shadow-glow/80 transition-all duration-300"
                  disabled={!terms || loading}
                >
                  {loading ? "Creando cuenta..." : "Crear Cuenta"}
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  ¿Ya tienes cuenta?{" "}
                  <Link
                    to="/login"
                    className="text-primary hover:text-primary-glow transition-colors"
                  >
                    Inicia sesión aquí
                  </Link>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}

export default Register
